// G-1 capture profile form on the Pursuit Overview tab.
//
// Opportunity name is the pursuit name (not duplicated). Current incumbent is
// the competition link with role=incumbent — this form shows that company as
// a convenience picker and writes the competition row, never a second id on
// the profile. Stage label is a filter label, not capture_projects.stage.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { useCompanyProfiles } from '../Delivery/Programs/referenceDataHooks';
import { BID_POSITIONS, CONTRACT_TYPES, STAGE_LABELS } from './constants';
import { useDeleteCaptureCompetitionLink, useUpsertCaptureCompetitionLink, useUpsertCaptureProfile } from './hooks';
const NONE = '__none__';
export function CaptureProfilePanel({ captureProjectId, pursuitName, profile, competitionLinks, correlationId }) {
    const upsert = useUpsertCaptureProfile(correlationId);
    const upsertCompetition = useUpsertCaptureCompetitionLink(correlationId);
    const deleteCompetition = useDeleteCaptureCompetitionLink(correlationId);
    const companies = useCompanyProfiles();
    const incumbent = competitionLinks.find((l)=>l.role === 'incumbent');
    const [solicitation, setSolicitation] = useState(profile?.solicitationNumber ?? '');
    const [crm, setCrm] = useState(profile?.crmNumber ?? '');
    const [bd, setBd] = useState(profile?.businessDeveloperId ?? '');
    const [cm, setCm] = useState(profile?.captureManagerId ?? '');
    const [customer, setCustomer] = useState(profile?.customerAgencyNames ?? '');
    const [bidPosition, setBidPosition] = useState(profile?.bidPosition ?? '');
    const [vehicle, setVehicle] = useState(profile?.vehicle ?? '');
    const [procurement, setProcurement] = useState(profile?.procurementType ?? '');
    const [contractType, setContractType] = useState(profile?.contractType ?? '');
    const [pop, setPop] = useState(profile?.periodOfPerformance ?? '');
    const [value, setValue] = useState(profile?.totalProgramValue ?? '');
    const [stageLabel, setStageLabel] = useState(profile?.stageLabel ?? '');
    const [pwin, setPwin] = useState(profile?.pwinPercent ?? '');
    const [risks, setRisks] = useState(profile?.risks ?? '');
    const [incumbentId, setIncumbentId] = useState(incumbent?.companyProfileId ?? '');
    useEffect(()=>{
        setSolicitation(profile?.solicitationNumber ?? '');
        setCrm(profile?.crmNumber ?? '');
        setBd(profile?.businessDeveloperId ?? '');
        setCm(profile?.captureManagerId ?? '');
        setCustomer(profile?.customerAgencyNames ?? '');
        setBidPosition(profile?.bidPosition ?? '');
        setVehicle(profile?.vehicle ?? '');
        setProcurement(profile?.procurementType ?? '');
        setContractType(profile?.contractType ?? '');
        setPop(profile?.periodOfPerformance ?? '');
        setValue(profile?.totalProgramValue ?? '');
        setStageLabel(profile?.stageLabel ?? '');
        setPwin(profile?.pwinPercent ?? '');
        setRisks(profile?.risks ?? '');
    }, [
        profile
    ]);
    useEffect(()=>{
        setIncumbentId(incumbent?.companyProfileId ?? '');
    }, [
        incumbent?.id,
        incumbent?.companyProfileId
    ]);
    const save = ()=>{
        upsert.mutate({
            captureProjectId,
            id: profile?.id,
            solicitationNumber: solicitation,
            crmNumber: crm,
            businessDeveloperId: bd,
            captureManagerId: cm,
            customerAgencyNames: customer,
            bidPosition,
            vehicle,
            procurementType: procurement,
            contractType,
            periodOfPerformance: pop,
            totalProgramValue: value,
            stageLabel,
            pwinPercent: pwin,
            risks
        });
        if (!incumbentId) {
            if (incumbent?.id) {
                deleteCompetition.mutate({
                    captureProjectId,
                    id: incumbent.id
                });
            }
        } else if (incumbentId !== incumbent?.companyProfileId) {
            upsertCompetition.mutate({
                captureProjectId,
                id: incumbent?.id,
                companyProfileId: incumbentId,
                role: 'incumbent'
            });
        }
    };
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": Testids.capture.profilePanel,
        children: [
            /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                className: "flex flex-row items-center justify-between space-y-0 py-4",
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardTitle, {
                        children: "Capture profile"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: save,
                        disabled: upsert.isPending || upsertCompetition.isPending || deleteCompetition.isPending,
                        "data-testid": Testids.capture.profileSaveButton,
                        children: upsert.isPending ? 'Saving…' : 'Save'
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "grid gap-3 sm:grid-cols-2",
                children: [
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Opportunity name",
                        children: /*#__PURE__*/ _jsx("p", {
                            className: "text-sm",
                            children: pursuitName
                        })
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Solicitation number",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: solicitation,
                            onChange: (e)=>setSolicitation(e.target.value),
                            "data-testid": Testids.capture.profileSolicitationInput
                        })
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "CRM #",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: crm,
                            onChange: (e)=>setCrm(e.target.value)
                        })
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Stage label",
                        children: /*#__PURE__*/ _jsxs(Select, {
                            value: stageLabel || NONE,
                            onValueChange: (v)=>setStageLabel(v === NONE ? '' : v),
                            children: [
                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                    "data-testid": Testids.capture.profileStageLabelSelect,
                                    children: /*#__PURE__*/ _jsx(SelectValue, {
                                        placeholder: "Filter label…"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(SelectContent, {
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectItem, {
                                            value: NONE,
                                            children: "None"
                                        }),
                                        STAGE_LABELS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    ]
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Business developer",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: bd,
                            onChange: (e)=>setBd(e.target.value),
                            placeholder: "User or person id"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Capture manager",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: cm,
                            onChange: (e)=>setCm(e.target.value),
                            placeholder: "User or person id"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Customer / agency",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: customer,
                            onChange: (e)=>setCustomer(e.target.value),
                            "data-testid": Testids.capture.profileCustomerInput
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Field, {
                        label: "Current incumbent",
                        children: [
                            /*#__PURE__*/ _jsxs(Select, {
                                value: incumbentId || NONE,
                                onValueChange: (v)=>setIncumbentId(v === NONE ? '' : v),
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        children: /*#__PURE__*/ _jsx(SelectValue, {
                                            placeholder: companies.isLoading ? 'Loading…' : 'Registry company…'
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(SelectContent, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: NONE,
                                                children: "None"
                                            }),
                                            (companies.data ?? []).map((c)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: c.id,
                                                    children: c.name
                                                }, c.id))
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "Stored as a competition link with role=incumbent."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Bid position",
                        children: /*#__PURE__*/ _jsxs(Select, {
                            value: bidPosition || NONE,
                            onValueChange: (v)=>setBidPosition(v === NONE ? '' : v),
                            children: [
                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                    "data-testid": Testids.capture.profileBidPositionSelect,
                                    children: /*#__PURE__*/ _jsx(SelectValue, {
                                        placeholder: "Prime / sub / JV"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(SelectContent, {
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectItem, {
                                            value: NONE,
                                            children: "None"
                                        }),
                                        BID_POSITIONS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    ]
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Vehicle",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: vehicle,
                            onChange: (e)=>setVehicle(e.target.value)
                        })
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Procurement type / set-aside",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: procurement,
                            onChange: (e)=>setProcurement(e.target.value)
                        })
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Contract type",
                        children: /*#__PURE__*/ _jsxs(Select, {
                            value: contractType || NONE,
                            onValueChange: (v)=>setContractType(v === NONE ? '' : v),
                            children: [
                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                    children: /*#__PURE__*/ _jsx(SelectValue, {
                                        placeholder: "T&M / FFP / …"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(SelectContent, {
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectItem, {
                                            value: NONE,
                                            children: "None"
                                        }),
                                        CONTRACT_TYPES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    ]
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Period of performance",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: pop,
                            onChange: (e)=>setPop(e.target.value)
                        })
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Total program value",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: value,
                            onChange: (e)=>setValue(e.target.value)
                        })
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "PWin %",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: pwin,
                            onChange: (e)=>setPwin(e.target.value)
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1 sm:col-span-2",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                children: "Risks / issues"
                            }),
                            /*#__PURE__*/ _jsx(SpecraTextarea, {
                                value: risks,
                                onChange: (e)=>setRisks(e.target.value),
                                rows: 3
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
function Field({ label, children }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1",
        children: [
            /*#__PURE__*/ _jsx("label", {
                className: "text-xs font-medium",
                children: label
            }),
            children
        ]
    });
}
